import { ReactNode } from "react";
import { GpmEntryMode } from "../../types/types";

type GpmModeSelectorProps = {
	mode: GpmEntryMode;
	knownGpm: string;
	onModeChange: (mode: GpmEntryMode) => void;
	onKnownGpmChange: (value: string) => void;
	helpDescription?: string;
};

export function GpmModeSelector({
	mode,
	knownGpm,
	onModeChange,
	onKnownGpmChange,
	helpDescription = "Not sure what flow rate you need? Select this option to estimate the required gallons per minute (GPM) based on your application details."
}: GpmModeSelectorProps) {
	return (
		<fieldset
			className="pf-gpm-mode-grid"
			aria-labelledby="gpm-mode-label"
		>
			<legend id="gpm-mode-label" className="pf-sr-only">
				GPM entry method
			</legend>
			<GpmModeCard
				title="I know my GPM"
				isActive={mode === "known"}
				onClick={() => onModeChange("known")}
			>
				<label className="pf-gpm-mode-card__field">
					<span className="pf-gpm-mode-card__field-label">GPM</span>
					<input
						type="number"
						className="pf-input"
						value={knownGpm}
						onChange={(event) => onKnownGpmChange(event.target.value)}
						onFocus={() => onModeChange("known")}
						min="0"
						step="any"
					/>
				</label>
			</GpmModeCard>

			<GpmModeCard
				title="Calculate GPM"
				isActive={mode === "help"}
				onClick={() => onModeChange("help")}
				describedBy="gpm-help-description"
			>
				<p id="gpm-help-description" className="pf-gpm-mode-card__description">
					{helpDescription}
				</p>
			</GpmModeCard>
		</fieldset>
	);
}

type GpmModeCardProps = {
	title: string;
	isActive: boolean;
	onClick: () => void;
	describedBy?: string;
	children: ReactNode;
};

function GpmModeCard({
	title,
	isActive,
	onClick,
	describedBy,
	children,
}: GpmModeCardProps) {
	const classes = [
		"pf-gpm-mode-card",
		isActive ? "pf-gpm-mode-card--active" : "",
	]
		.filter(Boolean)
		.join(" ");

	return (
		<div
			className={`pf-gpm-mode-option${isActive ? " pf-gpm-mode-option--active" : ""}`}
			onClick={onClick}
		>
			<button
				type="button"
				className={classes}
				aria-pressed={isActive}
				aria-describedby={describedBy}
			>
				<h3 className="pf-gpm-mode-card__title">{title}</h3>
			</button>
			{children}
		</div>
	);
}
